// Shared layout primitives for Glaze side-panel components (drawers built on
// `@base-ui/react`'s `DialogPrimitive`, e.g. `CampaignDetailSidebar.tsx` and
// `CampaignMemberSidebar.tsx`). Glaze supplies every form control and chrome
// piece these panels use (InputField / TextareaField / SwitchField / Button /
// ButtonLink / Badge / Alert / ModalHeader / ModalBody / ModalActions), so
// these two are the only local helpers needed — the shapes Glaze has no
// component for: a section header with a hairline rule, and a read-only
// label+value pair. Both take their exact metrics from the Paper artboards
// (file 01M0JHMCC35V9CKACWZB0NSKJS, light `1-0` / dark `5Y-0`), first
// authored inline in `CampaignDetailSidebar.tsx` and lifted here so a second
// panel doesn't have to duplicate them.
import type { ReactNode } from 'react';

/**
 * Section header: uppercase label + hairline rule. Composed rather than
 * imported because Glaze ships no section-rule component (verified against the
 * full `@clay-run/glaze/src` listing). Metrics from Paper nodes F-0/G-0/H-0.
 */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <h3 className="shrink-0 text-[11px]/[14px] font-semibold tracking-[0.06em] whitespace-nowrap text-content-tertiary uppercase">
          {title}
        </h3>
        <div className="h-px flex-1 bg-border-secondary" />
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

/**
 * Read-only label + value pair (Name, Type / Subtype). Metrics from Paper nodes
 * I-0/J-0/K-0 and L-0/M-0/N-0. Empty values render "—" rather than a blank —
 * callers should pass their own already-formatted display string (e.g.
 * "Yes"/"No" for a boolean).
 */
export function StaticField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px]/[12px] font-semibold tracking-[0.06em] text-content-tertiary uppercase">
        {label}
      </span>
      <p className="text-sm text-content-primary">{value || '—'}</p>
    </div>
  );
}
